import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { MemberRole } from '@dms/types';

export interface MembershipDealership {
  id: string;
  name: string;
  slug: string;
  logo: string | null;
}

export interface Membership {
  id: string;
  dealershipId: string;
  role: MemberRole;
  isOwner: boolean;
  status: string;
  permissionOverrides: Record<string, boolean> | null;
  /** null or empty = all locations in the workspace */
  locationIds: string[] | null;
  locations?: { id: string; name: string }[];
  dealership: MembershipDealership;
}

export interface AuthUser {
  id: string;
  supabaseId: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar: string | null;
  isSuperAdmin: boolean;
  lastActiveDealershipId: string | null;
  /** When true, sign-in skips the workspace picker when `lastActiveDealershipId` is still a valid membership. */
  autoOpenWorkspaceOnSignIn?: boolean;
}

interface AuthStore {
  user: AuthUser | null;
  memberships: Membership[];
  activeDealershipId: string | null;
  isSuperAdmin: boolean;

  activeMembership: () => Membership | null;
  setUser: (user: AuthUser | null) => void;
  setMemberships: (memberships: Membership[]) => void;
  setActiveDealership: (dealershipId: string) => void;
  setIsSuperAdmin: (val: boolean) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set, get) => ({
      user: null,
      memberships: [],
      activeDealershipId: null,
      isSuperAdmin: false,

      activeMembership: () => {
        const { memberships, activeDealershipId } = get();
        if (!activeDealershipId) return null;
        return memberships.find((m) => m.dealershipId === activeDealershipId) ?? null;
      },

      setUser: (user) => set({ user, isSuperAdmin: user?.isSuperAdmin ?? false }),

      setMemberships: (memberships) => set({ memberships }),

      setActiveDealership: (dealershipId) => {
        set({ activeDealershipId: dealershipId });
        // Persist to backend (fire-and-forget). Lazy-import api so this module never
        // statically imports lib/api (avoids api ↔ auth circular init in production chunks).
        void import('../lib/api').then(({ api }) =>
          api.patch('/users/me', { lastActiveDealershipId: dealershipId }).catch(() => {}),
        );
      },

      setIsSuperAdmin: (val) => set({ isSuperAdmin: val }),

      clear: () =>
        set({
          user: null,
          memberships: [],
          activeDealershipId: null,
          isSuperAdmin: false,
        }),
    }),
    {
      name: 'dms-auth',
      partialize: (state) => ({
        user: state.user,
        memberships: state.memberships,
        activeDealershipId: state.activeDealershipId,
        isSuperAdmin: state.isSuperAdmin,
      }),
      /**
       * Defer rehydration until after first client render. Without this,
       * persist reads localStorage synchronously during hydration — the server
       * rendered an empty store (no localStorage), so the first client render
       * with persisted data triggers a React hydration mismatch (most visible
       * on Topbar after logout → login as a different user, when localStorage
       * is repopulated by AuthSync before the user reaches /dashboard).
       *
       * `<AuthHydrator />` (mounted in app/layout.tsx) calls
       * `useAuthStore.persist.rehydrate()` from a useEffect, which kicks off
       * one extra render with the persisted values — matching the previous
       * behaviour minus the hydration warning.
       */
      skipHydration: true,
    },
  ),
);
